<template>
  <el-container>
    <el-main
      id="main-layout"
      :class="[
        getLayoutStyle,
        { assist_lesson: isAssistLesson },
        { discovery_lesson: isDiscoveryLesson }
      ]"
    >
      <el-row>
        <el-col
          id="video-column"
          :class="[isVideoScreenLayout ? 'left_contents' : 'right_contents']"
        >
          <el-row>
            <!-- Video Chat Component -->
            <VideoChat ref="videoChat" @reconnect="reRenderVideo" :key="componentKey" />

            <!-- Horizontal Slider -->
            <div @mousedown="adjustVertically" class="slider-horizontal">
              <div class="border"></div>
            </div>

            <!-- Chat Component -->
            <div :class="[isVideoScreenLayout ? 'right_contents' : '']">
              <Chatbox class="chat" ref="chatBox" :class="{ hide: materialSwitch !== 'chat' }" />
            </div>
          </el-row>
        </el-col>
        <el-col class="left_contents left_side" :class="classObject">
          <div class="material">
            <!-- Material Selection Component -->
            <MaterialControls />
            <!-- PDF Material Component -->
            <el-col class="left_text_content" :class="{ fullscreen: isMaterialFullScreen }">
              <MaterialControlsZoom />
              <PDFMaterial v-if="showPdf" />
              <DiscoveryMaterial v-if="isDiscoveryLesson" />
            </el-col>
          </div>
          <VocabularyBook v-if="showVocabularyBook" />
          <!-- Vertical Slider -->
          <div
            @mousedown="
              adjustHorizontally($event);
              enableOverlay();
            "
            @mouseup="disableOverlay"
            class="slider-vertical"
          >
            <div class="border"></div>
          </div>
        </el-col>
      </el-row>
    </el-main>
    <template v-if="!disableCFE">
      <ModalCustomerFeedback v-if="showModalCustomerFeedback" @close-dialog="closeCustomerfeedbackModal" />
    </template>
    <ModalPostAssessmentPromotion
      v-if="showPostAssessmentPromotion"
      :visible="showPostAssessmentPromotion"
      :banners="promotionData && promotionData.banners ? promotionData.banners : []"
      @close="closePostAssessmentPromotionModal"
    />
  </el-container>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import { providers } from '@/utils/constants';

import layoutAdjustment from '@/mixins/layoutAdjustment';
import cmsPreview from '@/mixins/cmsPreview';
import VideoChat from '@/components/Lesson/VideoChat';
import Chatbox from '@/components/Lesson/Chatbox';
import PDFMaterial from '@/components/Lesson/Material/PDFMaterial';
import DiscoveryMaterial from '@/components/Lesson/Material/DiscoveryMaterial';
import MaterialControls from '@/components/Lesson/Material/MaterialControls';
import MaterialControlsZoom from '@/components/Lesson/Material/MaterialControlsZoom';
import ModalCustomerFeedback from '@/components/Modal/CustomerFeedback';
import VocabularyBook from '@/components/Lesson/Material/VocabularyBook';
import { getApiMethod } from '@/modules/apiSelector';
import { NO_OP_QUERY } from '@/graphql/noOp.js';
import { GET_PLAN_BANNERS } from '@/graphql/planBanners';
import ModalPostAssessmentPromotion from '@/components/Modal/ModalPostAssessmentPromotion';
import { tsNow } from '@/utils/timeSync';
import { apolloClient as mbtiApolloClient } from '@/modules/MbtiGraphQL';

export default {
  mixins: [layoutAdjustment, cmsPreview],
  components: {
    VideoChat,
    Chatbox,
    PDFMaterial,
    DiscoveryMaterial,
    MaterialControls,
    MaterialControlsZoom,
    VocabularyBook,
    ModalCustomerFeedback,
    ModalPostAssessmentPromotion
  },
  props: ['showPdf'],
  data() {
    return {
      componentKey: 0, // use to rerender componect
      showModal: false,
      isConfigured: true, // 設定済みか（モック用）
      isPaidMember: true, // 有料会員か（モック用）
      isRecess: false, // 休会かどうか（モック用）,
      modalPromotionKey: 1,
      updatedTrainerSetting: null,
      promotionFetchTimer: null
    };
  },
  mounted() {
    this.setChatHeight();
    window.addEventListener('resize', this.setChatAndVideoSize);
    this.setupPromotionFetchTimer();
  },
  watch: {
    layoutStyle() {
      this.$nextTick(() => {
        this.setCurrrentLayoutStyle();
        this.resetAdjustedSize();
        this.setChatAndVideoSize();
      });
      this.reAdjustVideo();
    },
    async isLessonHasEnded(lessonStatus) {
      /* prettier-ignore */
      this.$logger.info('Lesson has ended, to proceed with checking of customer feedback modal requirements; lessonStatus=' + lessonStatus + ', isAssessmentLesson=' + this.isAssessmentLesson + ', isDisabled=' + this.customerFeedbackSetting?.is_disabled + ', isDue=' + this.customerFeedbackSetting?.is_due_for_display, { operation: 'modal' });

      if (lessonStatus) {
        this.handlePostAssessmentPromotion();
      }

      if (!lessonStatus || this.isAssessmentLesson) {
        this.$logger.info('Will not display instant customer feedback modal', { operation: 'modal' });
        return;
      }

      if (this.isCustomerFeedbackModalEnabled) {
        this.setShowModalCustomerFeedback(true);
        await this.logViewCustomerFeedback();
      }

      await this.incrementLessonHabit();

      if (!this.isCustomerFeedbackModalEnabled && this.isStudentCloseModal) {
        // PAP modal only applies to assessment lessons which return early above,
        // so if CFE is disabled (e.g. Zipan) and no modal will show, close directly.
        window.close();
      }
    }
  },
  computed: {
    ...mapState(['lessonId', 'lessonEnd']),
    ...mapState('layout', [
      'layoutStyle',
      'materialSwitch',
      'isMaterialFullScreen',
      'isMobileSize'
    ]),
    ...mapState('material', ['showVocabularyBook']),
    ...mapGetters(['isAssistLesson', 'isDiscoveryLesson', 'isAssessmentLesson', 'isZipanService']),
    ...mapGetters('modal', ['isCustomerFeedbackModalEnabled', 'isPromotionAvailable']),
    ...mapState('modal', [
      'isStudentCloseModal',
      'showModalCustomerFeedback',
      'customerFeedbackSetting',
      'showPostAssessmentPromotion',
      'promotionData',
      'promotionFetchAttempted'
    ]),
    ...mapState('lesson', ['isLessonHasEnded']),
    ...mapState('booking', ['selectedTrainerInfo', 'trainerInfo']),
    ...mapState(['teacherId', 'isTrial', 'isTest', 'provider', 'lessonToken', 'isDevTest']),
    ...mapState('videoRtc', ['rtcClient']),
    ...mapGetters('userOtherSetting', ['isTaiwanCountryId']),
    ...mapGetters('videoRtc', ['isUidConflict']),

    classObject() {
      return {
        hide: this.materialSwitch !== 'material',
        fullscreen: this.isMaterialFullScreen
      };
    },

    // get the current active layout style
    getLayoutStyle() {
      return `content_${this.layoutStyle}`;
    },

    disableCFE() {
      return (
        this.isTrial ||
        this.isTest ||
        this.isCmsPreview ||
        this.isTaiwanCountryId ||
        this.isDevTest ||
        this.isZipanService
      );
    },

    shouldFetchPromotion() {
      return this.isAssessmentLesson && !this.promotionFetchAttempted;
    }
  },
  methods: {
    ...mapMutations('layout', ['setIsVideoReloading', 'setVideoOverlayActivated']),
    ...mapMutations('connection', ['setReconnecting']),
    ...mapMutations('videoRtc', ['setIsShareScreenOn', 'setAudioOnly']),
    ...mapMutations('booking', [
      'setTrainerInfo',
      'setDiscoveryCategoryIds',
      'setDiscoveryCategoryDescription',
      'setSelectedTrainerInfo',
      'setSchedule',
      'setSelectedRankId',
      'setProgramId'
    ]),
    ...mapMutations('lessonHabit', ['setLessonHabit']),
    ...mapMutations('modal', [
      'setShowModalCustomerFeedback',
      'setCustomerFeedbackSetting',
      'setShowPostAssessmentPromotion',
      'setPromotionData',
      'setPromotionFetchStatus',
      'setPromotionFetchAttempted',
      'resetPromotionState'
    ]),

    disableOverlay() {
      this.setVideoOverlayActivated(false);
    },
    enableOverlay() {
      this.setVideoOverlayActivated(true);
    },
    async reRenderVideo() {
      this.$logger.info('Reconnect video', { operation: 'webrtc:reconnect' });
      this.setIsVideoReloading(true);
      this.setReconnecting(true);
      this.setAudioOnly(false);
      
      if (this.rtcClient && this.provider == providers.agora) {
        this.$logger.info('Disconnect from ' + this.provider, { operation: 'webrtc:reconnect' });
        try {
          await Promise.race([
            this.rtcClient.disconnect(),
            new Promise(resolve => setTimeout(resolve, 5000))
          ]);
        } catch (err) {
          this.$logger.warn('Disconnect failed or timed out', { operation: 'webrtc:reconnect', error: err.message });
        }
      }

      // force render VideoChat commonent to reconnect webrtc provider
      this.componentKey++;
      // reapply the adjusted size on video/chat
      this.reapplyVideoChatSize();
      // reset screen share
      this.setIsShareScreenOn(false);
    },
    closeCustomerfeedbackModal() {
      this.setShowModalCustomerFeedback(false);

      if (this.isStudentCloseModal) {
        window.close();
      }
    },
    async incrementLessonHabit() {
      let apiMethod = getApiMethod('incrementLessonHabit');
      await apiMethod(
        (response) => {
          if (response.data) {
            this.setLessonHabit(response.data.increment_lesson_habit);
          }
        },
        (error) => {
          console.error(error);
        }
      );
    },
    async logViewCustomerFeedback() {
      let apiMethod = getApiMethod('logCustomerFeedbackView');
      await apiMethod(this.lessonId);
    },
    setupPromotionFetchTimer() {
      if (!this.shouldFetchPromotion) {
        return;
      }

      const now = tsNow();
      const lessonEndTime = this.lessonEnd * 1000;
      const fiveMinutesBeforeEnd = lessonEndTime - (5 * 60 * 1000);
      const timeUntilFetch = fiveMinutesBeforeEnd - now;

      if (timeUntilFetch <= 0) {
        this.fetchPromotionData();
      } else {
        this.promotionFetchTimer = setTimeout(() => {
          this.fetchPromotionData();
        }, timeUntilFetch);
      }
    },
    async fetchPromotionData() {
      if (this.promotionFetchAttempted) {
        return;
      }

      this.setPromotionFetchAttempted(true);
      this.setPromotionFetchStatus('loading');

      try {
        const { data } = await mbtiApolloClient.query({
          query: GET_PLAN_BANNERS,
          fetchPolicy: 'no-cache'
        });

        this.setPromotionData(data.plan_banners);
        this.setPromotionFetchStatus('success');

        this.$logger.info('[PAP] Promotion data fetched successfully', {
          operation: 'pap-modal:server-log',
          event: 'fetch-success',
          banner_count: data.plan_banners?.banners?.length || 0,
          lesson_id: this.lessonId
        });
      } catch (error) {
        this.setPromotionFetchStatus('error');
        this.$logger.error('[PAP] Failed to fetch promotion data', {
          operation: 'pap-modal:server-log',
          event: 'fetch-error',
          error,
          lesson_id: this.lessonId
        });
      }
    },
    async handlePostAssessmentPromotion() {
      if (this.shouldFetchPromotion) {
        await this.fetchPromotionData();
      }

      if (this.shouldShowPostAssessmentPromotion()) {
        this.showPostAssessmentPromotionModal();
      } else if (this.isAssessmentLesson && this.isStudentCloseModal) {
        window.close();
      }
    },
    shouldShowPostAssessmentPromotion() {
      return this.isAssessmentLesson && this.isPromotionAvailable;
    },
    showPostAssessmentPromotionModal() {
      this.setShowPostAssessmentPromotion(true);
    },
    closePostAssessmentPromotionModal() {
      this.setShowPostAssessmentPromotion(false);

      if (this.isStudentCloseModal) {
        window.close();
      }
    }
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.setChatAndVideoSize);
    if (this.promotionFetchTimer) {
      clearTimeout(this.promotionFetchTimer);
    }
    this.resetPromotionState();
  },
  apollo: {
    customer_feedback_setting() {
      // In CMS-Preview apiMethod is null since CFE does not have legacy
      // so we will skip it immediatelly if its not applicable
      if (this.disableCFE) {
        return { skip: true, query: NO_OP_QUERY };
      }

      const apiMethod = getApiMethod('customerFeedbackSetting');
      const config = apiMethod(this.teacherId, (data) => {
        this.setCustomerFeedbackSetting(data);
      });

      return {
        ...config
      };
    }
  }
};
</script>
